// Traffic Attribution — captures UTM/source params on first landing and
// persists them for the lifetime of the install (sticky across sessions).
// Must NEVER throw; analytics layers depend on this returning a safe object.

const STORAGE_KEY = 'sfy_attribution';
const KNOWN_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign',
  'utm_content', 'utm_term', 'source', 'campaign',
];

const safeGet = (k) => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeSet = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* ignore */ }
};

const readFromUrl = () => {
  try {
    const sp = new URLSearchParams(window.location.search || '');
    const out = {};
    for (const k of KNOWN_KEYS) {
      const v = sp.get(k);
      if (v) out[k] = String(v).slice(0, 200);
    }
    return out;
  } catch {
    return {};
  }
};

const readReferrer = () => {
  try {
    const ref = document.referrer || '';
    return ref ? ref.slice(0, 200) : '';
  } catch { return ''; }
};

const readLandingPage = () => {
  try {
    return (window.location.pathname + window.location.search).slice(0, 200);
  } catch { return ''; }
};

/**
 * Capture attribution on app load. First touch wins (sticky) — but if the user
 * lands again with a NEW utm_source/source, we overwrite (last paid touch wins
 * for paid campaigns, which is the standard Google/Facebook tracking model).
 */
export const captureAttribution = () => {
  try {
    const fromUrl = readFromUrl();
    const stored = JSON.parse(safeGet(STORAGE_KEY) || '{}') || {};

    // If the new URL has a utm_source/source, treat it as a fresh touch.
    const hasNewTouch = !!(fromUrl.utm_source || fromUrl.source);
    if (hasNewTouch || Object.keys(stored).length === 0) {
      const merged = {
        ...(hasNewTouch ? {} : stored),
        ...fromUrl,
        referrer: readReferrer() || stored.referrer || '',
        landing_page: hasNewTouch
          ? readLandingPage()
          : (stored.landing_page || readLandingPage()),
        captured_at: new Date().toISOString(),
      };
      // Drop empty values
      Object.keys(merged).forEach((k) => {
        if (merged[k] == null || merged[k] === '') delete merged[k];
      });
      safeSet(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    }
    return stored;
  } catch {
    return {};
  }
};

/** Read currently-stored attribution. Returns {} when none. */
export const getAttribution = () => {
  try {
    return JSON.parse(safeGet(STORAGE_KEY) || '{}') || {};
  } catch { return {}; }
};

/** Header value used by axios/fetch when calling backend analytics endpoints. */
export const getAttributionHeaderValue = () => {
  const a = getAttribution();
  if (!a || Object.keys(a).length === 0) return '';
  try { return JSON.stringify(a); } catch { return ''; }
};
